import {
  Maybe,
  ReportDomainTypeEdge,
  ReportSectionFieldsTypeEdge,
  ReportSectionsTypeEdge,
} from "@api/graphql/types-and-hooks";
import { ReportFieldIdentifier, ReportSectionIdentifier } from "@components/PDFGenerator/Catalight/CatalightTypes";
import { preprocessShortTermGoalNode } from "@components/PDFGenerator/docxTemplateUtils";
import { convertReadableString } from "@components/forms/utils";
import { ReportNode } from "@providers/ReportProvider";

export enum GeneralizationCriteriaOptions {
  ACROSS_CONTEXTS = "Generalization Across Contexts",
  ACROSS_STIMULI = "Generalization Across Stimuli",
  EDUCATIONAL_PROVIDER = "Generalization to Education Provider / Aide",
  PARENTS = "Generalization to Parents / Caregivers",
  ALL = "Across Contexts / Environments / Stimuli / Educational Provider / Aide / Caregivers",
  NOT_APPLICABLE = "Not Applicable - Rationale",
}

type ReportGoalNode = NonNullable<
  NonNullable<NonNullable<NonNullable<ReportNode["reportTreatmentPlan"]>["reportgoalSet"]>["edges"][number]>["node"]
>;

export interface OrganizedCatalightGoals<TGoal = ReportGoalNode> {
  receptiveCommunicationGoals: TGoal[];
  expressiveCommunicationGoals: TGoal[];
  pragmaticCommunicationGoals: TGoal[];
  selfHelpDailyLivingSkills: TGoal[];
  behaviorGoals: TGoal[];
  caregiverTrainingGoals: TGoal[];
}

const goalTemplateNameMap = {
  "Receptive Communication": "receptiveCommunicationGoals",
  "Receptive Communication Goals": "receptiveCommunicationGoals",

  "Expressive Communication": "expressiveCommunicationGoals",
  "Expressive Communication Goals": "expressiveCommunicationGoals",

  "Pragmatic Communication": "pragmaticCommunicationGoals",
  "Pragmatic Communication Goals": "pragmaticCommunicationGoals",

  "Self Help / Daily Living Skills": "selfHelpDailyLivingSkills",
  "Self Help / Daily Living Skills Goals": "selfHelpDailyLivingSkills",

  Behavior: "behaviorGoals",
  "Behavior Goals": "behaviorGoals",

  "Caregiver Training": "caregiverTrainingGoals",
  "Caregiver Training Goals": "caregiverTrainingGoals",
};
// Add this helper function to extract the first assessment date
const getFirstAssessmentDate = (reportData: ReportNode): string | null => {
  try {
    const recordReviewEdges = getSectionObjectBySectionIdentifier(
      reportData?.reportSections?.edges,
      "Catalight_Record_Review",
    )?.node?.sectionField?.edges;

    const assessmentAppointmentsContent = getFieldContentByFieldIdentifier(
      recordReviewEdges,
      "Assessment_Appointments",
    );

    if (!assessmentAppointmentsContent) return null;

    const assessmentData = JSON.parse(assessmentAppointmentsContent);
    const firstRow = assessmentData?.data?.[0];

    if (firstRow && firstRow.Date) {
      // Convert date to proper format if needed
      const date = new Date(firstRow.Date);
      if (!isNaN(date.getTime())) {
        // Format as M/d/yyyy (e.g., "3/2/2025")
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      }
    }

    return null;
  } catch (error) {
    console.error("Error extracting first assessment date:", error);
    return null;
  }
};

function organizeCatalightGoals<
  TDomain extends { node?: Pick<NonNullable<ReportDomainTypeEdge["node"]>, "id" | "name"> | null },
  TGoal extends Partial<ReportGoalNode>,
>(
  reportDomains: Maybe<TDomain>[],
  shortTermGoals: Maybe<{ node?: TGoal | null }>[],
  baselineDate?: string | null,
): OrganizedCatalightGoals<TGoal> {
  const result: OrganizedCatalightGoals<TGoal> = {
    receptiveCommunicationGoals: [],
    expressiveCommunicationGoals: [],
    pragmaticCommunicationGoals: [],
    selfHelpDailyLivingSkills: [],
    behaviorGoals: [],
    caregiverTrainingGoals: [],
  };

  // Create a map of report domain IDs to their categories
  const reportDomainCategories = new Map<string, keyof OrganizedCatalightGoals<TGoal>>();
  if (reportDomains) {
    reportDomains.forEach((goalEdge) => {
      if (!goalEdge?.node) return;

      const { id, name } = goalEdge.node;
      if (!id || !name) return;

      // @ts-ignore: Ignoring the compiler and risking bugs because: This is a legit dict
      const templateKey = goalTemplateNameMap[name] as keyof OrganizedCatalightGoals<TGoal>;
      if (!templateKey) return;
      reportDomainCategories.set(id, templateKey);
    });
  }

  if (shortTermGoals) {
    shortTermGoals.forEach((goalEdge) => {
      if (!goalEdge?.node) return;

      const processedNode = preprocessShortTermGoalNode(goalEdge.node);

      // Populate baseline date if not already set and we have a baseline date from assessment
      if (baselineDate && !processedNode.baselineDate) {
        processedNode.baselineDate = baselineDate;
      }

      const { reportDomain } = processedNode;
      if (!reportDomain?.id) return;

      const category = reportDomainCategories.get(reportDomain.id);
      if (category) {
        result[category].push(processedNode);
      }
    });
  }

  return result;
}

export const organizeCatalightTreatmentPlanGoals = (reportData: ReportNode) => {
  const reportDomains = reportData.reportTreatmentPlan?.reportdomainSet?.edges || [];
  const shortTermGoals = reportData.reportTreatmentPlan?.reportgoalSet?.edges || [];
  const goalsInUse = shortTermGoals.filter((goalEdge) => goalEdge?.node?.inUse);

  // Extract the first assessment date for baseline date population
  const firstAssessmentDate = getFirstAssessmentDate(reportData);

  return organizeCatalightGoals(reportDomains, goalsInUse, firstAssessmentDate);
};

type InputContent = {
  customRowAttributeField: string;
  data: Array<{
    [key: string]: string | string[] | undefined;
    Domain: string;
    "Standard Score": string;
    "V-Scale Score": string;
    "Adaptive Level": string;
    "Percentile Rank": string;
    "Age Equivalent (optional)": string;
    customRowAttributeField?: string[];
  }>;
  headers: string[];
};

export type Dsm5Row = {
  id: string;
  description: string;
  isAllowedNote: boolean;
  isCheckedDisabled: boolean;
  isChecked: boolean;
  note: string;
  parentId: string;
};

export type DsmSpecifierRow = {
  id: string;
  category: string;
  note: string;
  options: DsmSpecifierOption[];
  specifier: string;
};

type DsmSpecifierOption = {
  id: string;
  label: string;
  isChecked: boolean;
};

export type TransformedData = {
  [key: string]: string | undefined | boolean;
};

export const transformVinelandFormData = (content: string): TransformedData | null => {
  if (!content || !content.length) return null;
  try {
    const parsed = JSON.parse(content);
    if (!parsed.data || !Array.isArray(parsed.data) || parsed.data.length === 0) return null;
    const firstRow = parsed.data[0];
    const result: Record<string, string> = {};

    Object.keys(firstRow).forEach((key) => {
      const value = firstRow[key];
      if (!value || typeof value !== "string") {
        console.warn("Invalid item found:", value);
        return;
      }

      // Convert the key to camelCase
      const camelCaseKey = key
        .toLowerCase()
        // Remove special characters and spaces, capitalize next letter
        .replace(/[^a-zA-Z0-9]+(.)/g, (_: unknown, char: string) => char.toUpperCase())
        // Handle the case where the string starts with a number
        .replace(/^[0-9]/, "");

      result[camelCaseKey] = value;
    });

    return result;
  } catch (error) {
    console.error("Error transforming content:", error);
    throw new Error(`Failed to transform content: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
};

export const transformVinelandTableData = (content: string): TransformedData | null => {
  if (!content || !content.length) return null;
  try {
    const parsed: InputContent = JSON.parse(content);
    const result: TransformedData = {
      //vineland optional fields should go here (to prevent undefined in output)
    };

    const defaultDomain = [
      "motorSkillsOptional)",
      "fineMotor",
      "grossMotor",
      "maladaptiveBehaviorOptional)",
      "internalizing",
      "externalizing",
      "communication",
      "receptive",
      "expressive",
      "dailyLivingSkills",
      "personal",
      "domestic",
      "community",
      "socialization",
      "interpersonalRelationships",
      "playAndLeisure",
      "copingSkills",
      "maladaptiveBehaviorOptional)",
      "other",
      "adaptiveBehaviorComposite",
    ];

    // adding all the expected vineland fields (to prevent undefined in output)
    defaultDomain.forEach((domainKey) => {
      result[`${domainKey}StandardScore`] = "-";
      result[`${domainKey}VScaleScore`] = "-";
      result[`${domainKey}AdaptiveLevel`] = "-";
      result[`${domainKey}PercentileRank`] = "-";
      result[`${domainKey}AgeEquivalent`] = "-";
      result[`${domainKey}ConfidenceInterval`] = "-";
    });

    // if Domain value is not there then it means it has wrong/unsupported vineland table
    // Question: Do we need to support the other table?
    if (!parsed.data[0].Domain) {
      return result;
    }

    // Process each data entry
    parsed.data.forEach((item) => {
      let domainKey = item.Domain.toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
        .replace(/\s/g, "");

      // Vineland-3 labels this composite domain "Motor Skills" for younger clients and
      // "Motor Skills (Optional)" for older clients (where it's optional). Normalize both
      // to the same export key so the template only needs a single tag per cell.
      if (domainKey === "motorSkills") {
        domainKey = "motorSkillsOptional)";
      }

      const confidenceIntervalKey =
        Object.keys(item).find((key) => key.toLowerCase().includes("confidence interval")) || "Confidence Interval";

      // Add each metric as a separate property
      result[`${domainKey}StandardScore`] = item["Standard Score"] || "-";
      result[`${domainKey}VScaleScore`] = item["V-Scale Score"] || "-";
      result[`${domainKey}AdaptiveLevel`] = item["Adaptive Level"] || "-";
      result[`${domainKey}PercentileRank`] = item["Percentile Rank"] || "-";
      result[`${domainKey}AgeEquivalent`] = item["Age Equivalent (optional)"] || "-";

      // add the confidence interval to returned keys
      result["confidenceIntervalKey"] = confidenceIntervalKey;
      result[`${domainKey}ConfidenceInterval`] = String(item[confidenceIntervalKey]) || "-";
    });
    return result;
  } catch (error) {
    throw new Error(`Failed to parse or transform vineland table content: ${error}`);
  }
};

/**
 * returns the severity levels based on the selection for DSM domain A and B
 * @param domainA the domain A severity level
 * @param domainB the domain B severity level
 * @returns the transformed table data with the additional boolean keys
 */
export const dsmSeverityLevels = (domainA: string, domainB: string): TransformedData => {
  const safeA = typeof domainA === "string" ? domainA.toLowerCase() : "";
  const safeB = typeof domainB === "string" ? domainB.toLowerCase() : "";
  return {
    isALevel1: safeA.includes("level 1"),
    isALevel2: safeA.includes("level 2"),
    isALevel3: safeA.includes("level 3"),
    isBLevel1: safeB.includes("level 1"),
    isBLevel2: safeB.includes("level 2"),
    isBLevel3: safeB.includes("level 3"),
  };
};

/**
 * this function parses the DSM specifier table into an array of readable strings
 * @param tableContent the dsm5 specifier table content
 * @returns an array of the formatted specifier strings
 */
export const parseDsmSpecifiers = (tableContent: DsmSpecifierRow[]): string[] => {
  if (!Array.isArray(tableContent)) return [];

  const specifiers: string[] = [];

  // loop through each specifier row and generate output string based on selected options
  tableContent.forEach((specifier) => {
    let formatted_specifier = "";

    // determine checked option
    specifier.options.forEach((option) => {
      if (!option.isChecked) return;

      formatted_specifier += option.label;
    });

    // skip if nothing was checked
    if (!formatted_specifier) return;

    // add specifier label
    formatted_specifier += ` ${specifier.specifier}`;

    // add notes, if applicable
    if (specifier.note) {
      formatted_specifier += ` - ${specifier.note}`;
    }

    specifiers.push(convertReadableString(formatted_specifier));
  });

  return specifiers;
};

export const getSectionObjectBySectionIdentifier = <
  TSection extends { node?: Pick<NonNullable<ReportSectionsTypeEdge["node"]>, "sectionIdentifier"> | null },
>(
  sectionsEdges: Array<Maybe<TSection>> = [],
  sectionIdentifier: ReportSectionIdentifier,
) => {
  return sectionsEdges.find((edge) => edge?.node?.sectionIdentifier === sectionIdentifier) ?? null;
};

export const getFieldContentByFieldIdentifier = <
  TField extends {
    node?: Pick<NonNullable<ReportSectionFieldsTypeEdge["node"]>, "fieldIdentifier" | "content"> | null;
  },
>(
  fieldsEdges: Array<Maybe<TField>> = [],
  fieldIdentifier: ReportFieldIdentifier,
) => {
  return fieldsEdges.find((edge) => edge?.node?.fieldIdentifier === fieldIdentifier)?.node?.content ?? "";
};

export const getImageContentBase64FromField = <
  TField extends {
    node?: Pick<NonNullable<ReportSectionFieldsTypeEdge["node"]>, "fieldIdentifier" | "imageContentBase64"> | null;
  },
>(
  sectionEdges: Array<Maybe<TField>> = [],
  fieldIdentifier: ReportFieldIdentifier,
): Maybe<string | undefined> => {
  const imageField = sectionEdges?.find((edge) => edge?.node?.fieldIdentifier === fieldIdentifier);
  const base64 = imageField?.node?.imageContentBase64;
  return base64;
};
